import { useMemo, useState } from 'react';
import type { Movie } from '@/mocks/movies';
import { genreOptions, regionOptions, yearOptions, sortOptions } from '@/mocks/movies';
import { filterMovies, sortMovies } from '@/utils/media';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/base/Pagination';

interface MovieBrowserProps {
  movies: Movie[];
  showFilters?: boolean;
  pageSize?: number;
  emptyText?: string;
}

const chipBase =
  'px-3 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer';

export default function MovieBrowser({
  movies,
  showFilters = true,
  pageSize = 12,
  emptyText = '暂无相关内容',
}: MovieBrowserProps) {
  const [genre, setGenre] = useState('全部');
  const [region, setRegion] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState('latest');
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () => sortMovies(filterMovies(movies, { genre, region, year }), sort),
    [movies, genre, region, year, sort],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const hasFilter = genre !== '全部' || region !== '全部' || year !== '全部';
  const resetFilters = () => {
    setGenre('全部');
    setRegion('全部');
    setYear('全部');
    setSort('latest');
    setPage(1);
  };

  const update = (fn: () => void) => {
    fn();
    setPage(1);
  };

  const renderChips = (
    options: string[],
    value: string,
    setValue: (v: string) => void,
    prefix: string,
  ) => (
    <div className="flex items-center gap-2">
      <span className="shrink-0 w-10 text-xs text-foreground-500">{prefix}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => update(() => setValue(opt))}
            className={`${chipBase} ${
              value === opt
                ? 'bg-primary-500 text-background-50 font-medium'
                : 'bg-background-200 text-foreground-700 hover:bg-background-300'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      {showFilters && (
        <div className="rounded-lg bg-background-100 border border-background-300/60 p-4 md:p-5 space-y-4">
          {renderChips(genreOptions, genre, setGenre, '类型')}
          {renderChips(regionOptions, region, setRegion, '地区')}
          {renderChips(yearOptions, year, setYear, '年份')}
          <div className="flex items-center justify-between gap-4 pt-2 border-t border-background-300/60">
            <div className="flex items-center gap-2">
              <span className="shrink-0 w-10 text-xs text-foreground-500">排序</span>
              <div className="flex flex-wrap gap-2">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => update(() => setSort(opt.key))}
                    className={`${chipBase} ${
                      sort === opt.key
                        ? 'bg-accent-500 text-background-50 font-medium'
                        : 'bg-background-200 text-foreground-700 hover:bg-background-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {hasFilter && (
              <button
                type="button"
                onClick={resetFilters}
                className="shrink-0 text-xs text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line mr-1" />
                清空筛选
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mt-5 flex items-center justify-between">
        <p className="text-sm text-foreground-500">
          共 <span className="text-accent-500 font-semibold">{filtered.length}</span> 部作品
        </p>
        <p className="text-xs text-foreground-500 hidden sm:block">
          第 {currentPage} / {totalPages} 页
        </p>
      </div>

      {pageItems.length > 0 ? (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {pageItems.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      ) : (
        <div className="mt-6 rounded-lg bg-background-100 border border-background-300/60 py-16 flex flex-col items-center text-center">
          <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center text-3xl text-foreground-500">
            <i className="ri-inbox-archive-line" />
          </span>
          <p className="mt-4 text-foreground-700">{emptyText}</p>
          <p className="mt-1 text-sm text-foreground-500">试试调整或清空筛选条件</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-5 px-5 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            清空筛选
          </button>
        </div>
      )}

      <Pagination current={currentPage} total={totalPages} onChange={setPage} />
    </div>
  );
}